import type { AppState, Notification, Task, User } from '../types';
import { generateId } from './idGenerator';
import { parseDateLike } from './dateUtils';

const toTime = (value: unknown, fallback: number = Number.NaN): number => {
  const parsedDate = parseDateLike(value);
  return parsedDate ? parsedDate.getTime() : fallback;
};

/**
 * Check if user has tasks in the next 15 days
 */
export function checkTaskPlanningFor15Days(userId: string, state: AppState): boolean {
  const now = new Date();
  const fifteenDaysFromNow = new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000);
  const nowTime = now.getTime();
  const fifteenDaysTime = fifteenDaysFromNow.getTime();
  
  const userTasks = state.tasks.filter(task => 
    task.assigneeIds.includes(userId) &&
    task.status !== 'Completed' &&
    task.organizationId === state.currentOrganization.id
  );

  // Check if there are tasks with startDate or targetEndDate in next 15 days
  const tasksInNext15Days = userTasks.filter(task => {
    const startDateTime = toTime(task.startDate);
    if (!Number.isNaN(startDateTime) && startDateTime <= fifteenDaysTime && startDateTime >= nowTime) {
      return true;
    }

    const targetEndDateTime = toTime(task.targetEndDate);
    if (!Number.isNaN(targetEndDateTime) && targetEndDateTime <= fifteenDaysTime && targetEndDateTime >= nowTime) {
      return true;
    }

    return false;
  });

  return tasksInNext15Days.length > 0;
}

/**
 * Check if user needs standup reminder (no standup in last 24 hours)
 */
export function checkStandupReminder(userId: string, state: AppState): boolean {
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const oneDayAgoTime = oneDayAgo.getTime();
  
  const recentStandups = state.standupNotes.filter(note => 
    note.authorId === userId &&
    note.organizationId === state.currentOrganization.id &&
    toTime(note.createdAt) > oneDayAgoTime
  );

  return recentStandups.length === 0;
}

/**
 * Check if user needs time log reminder (no time logs for tasks in last 24 hours)
 */
export function checkTimeLogReminder(userId: string, state: AppState): boolean {
  const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  const oneDayAgoTime = oneDayAgo.getTime();
  
  const userActiveTasks = state.tasks.filter(task => 
    task.assigneeIds.includes(userId) &&
    (task.status === 'In Progress' || task.status === 'Active') &&
    task.organizationId === state.currentOrganization.id
  );

  if (userActiveTasks.length === 0) return false;

  // Check if any active task has recent time logs
  const hasRecentTimeLogs = userActiveTasks.some(task => 
    task.timeLogs.some(log => 
      log.userId === userId &&
      toTime(log.date) > oneDayAgoTime
    )
  );

  return !hasRecentTimeLogs;
}

/**
 * Generate automatic notifications for users
 */
export function generateAutomaticNotifications(state: AppState): Notification[] {
  const notifications: Notification[] = [];
  const orgUsers = state.users.filter(u => u.organizationId === state.currentOrganization.id);

  orgUsers.forEach(user => {
    // Check for task planning
    if (!checkTaskPlanningFor15Days(user.id, state)) {
      notifications.push({
        id: generateId('notif', user.id, 'task-planning-needed'),
        organizationId: state.currentOrganization.id,
        userId: user.id,
        type: 'task_planning_needed',
        priority: 'high',
        title: 'No Tasks Planned for Next 15 Days',
        message: 'You don\'t have any tasks assigned for the next 15 days. Please plan and add tasks to your queue.',
        read: false,
        createdAt: new Date().toISOString(),
        actionUrl: '/app/projects'
      });
    }

    // Check for standup reminder
    if (checkStandupReminder(user.id, state)) {
      notifications.push({
        id: generateId('notif', user.id, 'standup-reminder'),
        organizationId: state.currentOrganization.id,
        userId: user.id,
        type: 'standup_reminder',
        priority: 'medium',
        title: 'Daily Standup Reminder',
        message: 'Please provide your daily standup update. What did you work on? What are you working on today?',
        read: false,
        createdAt: new Date().toISOString(),
        actionUrl: '/app'
      });
    }

    // Check for time log reminder
    if (checkTimeLogReminder(user.id, state)) {
      notifications.push({
        id: generateId('notif', user.id, 'timelog-reminder'),
        organizationId: state.currentOrganization.id,
        userId: user.id,
        type: 'timelog_reminder',
        priority: 'medium',
        title: 'Time Log Reminder',
        message: 'Don\'t forget to log your hours for today. Accurate time tracking helps with project planning.',
        read: false,
        createdAt: new Date().toISOString(),
        actionUrl: '/app/timesheet'
      });
    }
  });

  return notifications;
}

/**
 * Get all pinned messages visible to a user (from projects, tasks, sprints they're involved in)
 */
export function getPinnedMessagesForUser(userId: string, state: AppState) {
  const user = state.users.find(u => u.id === userId);
  if (!user) return [];

  const allPinnedMessages: Array<{ message: any; source: string; entityId: string; entityTitle: string }> = [];

  // Get pinned messages from user's projects
  const userProjects = state.projects.filter(p => 
    p.organizationId === state.currentOrganization.id &&
    (p.teamMembers.some(tm => tm.userId === userId) || !p.allowedUserIds || p.allowedUserIds.includes(userId))
  );

  userProjects.forEach(project => {
    if (project.pinnedMessages) {
      project.pinnedMessages.forEach(msg => {
        // Filter by role and expiration
        const expiresAtTime = toTime(msg.expiresAt);
        const isExpired = !Number.isNaN(expiresAtTime) && expiresAtTime < Date.now();
        const isRoleMatch = !msg.targetRoles || msg.targetRoles.length === 0 || msg.targetRoles.includes(user.role);
        
        if (!isExpired && isRoleMatch) {
          allPinnedMessages.push({
            message: msg,
            source: 'project',
            entityId: project.id,
            entityTitle: project.name
          });
        }
      });
    }
  });

  // Get pinned messages from user's tasks
  const userTasks = state.tasks.filter(t => 
    t.assigneeIds.includes(userId) &&
    t.organizationId === state.currentOrganization.id
  );

  userTasks.forEach(task => {
    if (task.pinnedMessages) {
      task.pinnedMessages.forEach(msg => {
        const expiresAtTime = toTime(msg.expiresAt);
        const isExpired = !Number.isNaN(expiresAtTime) && expiresAtTime < Date.now();
        const isRoleMatch = !msg.targetRoles || msg.targetRoles.length === 0 || msg.targetRoles.includes(user.role);
        
        if (!isExpired && isRoleMatch) {
          allPinnedMessages.push({
            message: msg,
            source: 'task',
            entityId: task.id,
            entityTitle: task.title
          });
        }
      });
    }
  });

  // Get pinned messages from sprints
  const projectIds = userProjects.map(p => p.id);
  const sprints = state.sprints.filter(s => projectIds.includes(s.projectId));

  sprints.forEach(sprint => {
    if (sprint.pinnedMessages) {
      sprint.pinnedMessages.forEach(msg => {
        const expiresAtTime = toTime(msg.expiresAt);
        const isExpired = !Number.isNaN(expiresAtTime) && expiresAtTime < Date.now();
        const isRoleMatch = !msg.targetRoles || msg.targetRoles.length === 0 || msg.targetRoles.includes(user.role);
        
        if (!isExpired && isRoleMatch) {
          allPinnedMessages.push({
            message: msg,
            source: 'sprint',
            entityId: sprint.id,
            entityTitle: sprint.name
          });
        }
      });
    }
  });

  // Sort by priority and date
  const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
  allPinnedMessages.sort((a, b) => {
    const priorityDiff = priorityOrder[a.message.priority as keyof typeof priorityOrder] - 
                        priorityOrder[b.message.priority as keyof typeof priorityOrder];
    if (priorityDiff !== 0) return priorityDiff;
    return toTime(b.message.createdAt, 0) - toTime(a.message.createdAt, 0);
  });

  return allPinnedMessages;
}